import React, {useEffect} from 'react';
import {Grid} from '@mui/material';
import {useLazyQuery} from '@apollo/client';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {Button} from '../../components/Button/Button';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {
  ReportPackTemplateATRTypeEnum,
  ReportPackTemplateDocumentTypeEnum,
  ReportPackTemplateNewOrExistingClientEnum,
} from '../../schema-types/globalTypes';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import {getNoActionLetterHtmlQuery} from './reportPackQueries';
import {
  GetNoActionLetterHtml,
  GetNoActionLetterHtmlVariables,
} from '../../schema-types/GetNoActionLetterHtml';
import {useReportPack} from './useReportPack';
import {useReviewContext} from '../../contexts/ReviewContext';
import {UPDATES} from '../../shared/UPDATES';

interface NoActionOptionsProps {
  reportPack: ReportPackFragment;
  onTemplateHtmlChange: (value: string) => void;
}

export const NoActionOptions: React.FC<NoActionOptionsProps> = ({
  reportPack,
  onTemplateHtmlChange,
}) => {
  const [{value: clientType}, _, {setValue: setClientType}] =
    useAutoField<ReportPackTemplateNewOrExistingClientEnum>('clientType');
  const [{value: atrType}, __, {setValue: setAtrType}] =
    useAutoField<ReportPackTemplateATRTypeEnum>('atrType');
  const [{value: includeOptions}, ___, {setValue: setIncludeOptions}] =
    useAutoField<ReportPackTemplateDocumentTypeEnum[]>('includeOptions');

  const [fetchHtml] = useLazyQuery<GetNoActionLetterHtml, GetNoActionLetterHtmlVariables>(
    getNoActionLetterHtmlQuery
  );
  const {reviewId} = useReviewContext();
  const {onUpsert} = useReportPack();

  const handleCreateTemplate = () => {
    if (!clientType || !atrType) {
      return;
    }

    fetchHtml({
      variables: {
        reportPackId: reportPack.id,
        formType: reportPack.templateType!,
        newOrExisting: clientType,
        atrType,
        documentTypes: includeOptions ?? [],
      },
    }).then(async (response) => {
      if (response.data && response.data.getNoActionLetterHtml) {
        onTemplateHtmlChange(response.data.getNoActionLetterHtml);
        await onUpsert({
          variables: {
            reviewId,
            reportPackId: reportPack.id,
            templateNewOrExistingClient: clientType,
            templateAtrType: atrType,
            templateDocumentTypes: includeOptions,
          },
        });
      }
    });
  };

  useEffect(() => {
    if (!clientType && reportPack.templateNewOrExistingClient) {
      setClientType(UPDATES._2102, reportPack.templateNewOrExistingClient);
    }
    if (!atrType && reportPack.templateAtrType) {
      setAtrType(UPDATES._2103, reportPack.templateAtrType);
    }
    if (!includeOptions?.length && reportPack.templateDocumentTypes?.length) {
      setIncludeOptions(
        UPDATES._2104,
        reportPack.templateDocumentTypes.filter((x) => !!x)
      );
    }
  }, [reportPack, clientType, atrType, includeOptions]);

  return (
    <>
      <Grid pt={2} pl={2} container spacing={3}>
        <Grid item xs={6} lg={3} borderRight={1} borderColor="divider">
          <AutoField
            field={{
              id: 'clientType',
              label: 'Client type',
              boldLabel: true,
              type: ActionType.radioGroup,
              horizontal: true,
              horizontalOptionWidthXs: 6,
              fullWidth: true,
              options: [
                {
                  label: 'New Client',
                  value: ReportPackTemplateNewOrExistingClientEnum.NEW,
                },
                {
                  label: 'Existing Client',
                  value: ReportPackTemplateNewOrExistingClientEnum.EXISTING,
                },
              ],
            }}
          />
        </Grid>
        <Grid item xs={6} lg={4} borderRight={1} borderColor="divider">
          <AutoField
            field={{
              id: 'atrType',
              label: 'ATR alignment',
              type: ActionType.radioGroup,
              horizontal: true,
              size: 'medium',
              fullWidth: true,
              boldLabel: true,
              horizontalOptionWidthXs: 4,
              options: [
                {
                  label: 'No ATR',
                  value: ReportPackTemplateATRTypeEnum.NO_ATR,
                },
                {
                  label: 'Aligned ATR',
                  value: ReportPackTemplateATRTypeEnum.ALIGNED_ATR,
                },
                {
                  label: 'Misaligned ATR',
                  value: ReportPackTemplateATRTypeEnum.MISALIGNED_ATR,
                },
              ],
            }}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <AutoField
            field={{
              id: 'includeOptions',
              label: 'Include:',
              type: ActionType.checkboxGroup,
              horizontal: true,
              fullWidth: true,
              boldLabel: true,
              horizontalOptionWidthXs: 4,
              options: [
                {
                  label: 'Power of Attorney',
                  value: ReportPackTemplateDocumentTypeEnum.POA,
                },
                {
                  label: 'Expression of Wishes',
                  value: ReportPackTemplateDocumentTypeEnum.EOW,
                },
                {
                  label: 'Tasks',
                  value: ReportPackTemplateDocumentTypeEnum.TASKS,
                },
                {
                  label: 'Will',
                  value: ReportPackTemplateDocumentTypeEnum.WILL,
                },
              ],
            }}
          />
        </Grid>
      </Grid>
      <Button
        eventName="create-report-html"
        variant="contained"
        type="submit"
        sx={{marginTop: 1, ml: 2, mb: 2}}
        disabled={!clientType || !atrType}
        onClick={handleCreateTemplate}
      >
        Create template
      </Button>
    </>
  );
};
